import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/authStore";
import { canUseEditMode } from "./editAccess";
import { PRODUCT_LABELS, buildAgentBrief } from "./elementCapture";
import Shot from "./EditRequestShot";
import "./EditRequestsPage.css";

const STATUSES = [
  ["", "All statuses"],
  ["new", "New"],
  ["in_progress", "In progress"],
  ["done", "Done"],
  ["wont_do", "Won't do"],
];

const PRODUCTS = [
  ["", "All products"],
  ...Object.entries(PRODUCT_LABELS).filter(([key]) => key !== "app"),
];

export default function EditRequestsPage() {
  const user = useAuth((state) => state.user);
  const scope = useAuth((state) => state.scope);
  const allowed = canUseEditMode(user, scope);
  const [status, setStatus] = useState("");
  const [product, setProduct] = useState("");
  const [route, setRoute] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [prLink, setPrLink] = useState("");
  const [nextStatus, setNextStatus] = useState("in_progress");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const detailRef = useRef(null);

  useEffect(() => {
    const article = detailRef.current;
    if (!article) return undefined;
    const sizeToViewport = () => {
      const available = Math.max(240, window.innerHeight - article.getBoundingClientRect().top - 16);
      article.style.setProperty("--er-detail-height", `${available}px`);
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(sizeToViewport);
    observer?.observe(article.closest(".er-page"));
    window.addEventListener("resize", sizeToViewport);
    sizeToViewport();
    return () => { observer?.disconnect(); window.removeEventListener("resize", sizeToViewport); };
  }, [detail?.id]);

  const load = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (product) params.product = product;
    if (route.trim()) params.route = route.trim();
    const { data } = await api.get("/edit-requests", { params });
    setRows(Array.isArray(data) ? data : []);
    setSelectedId((current) => current || data?.[0]?.id || "");
  }, [product, route, status]);

  useEffect(() => {
    if (!allowed) return undefined;
    let alive = true;
    load().catch((loadError) => {
      if (!alive) return;
      setError(loadError?.response?.data?.detail || "Could not load edit requests.");
    });
    return () => { alive = false; };
  }, [allowed, load]);

  useEffect(() => {
    if (!allowed || !selectedId) {
      setDetail(null);
      return undefined;
    }
    let alive = true;
    api.get(`/edit-requests/${selectedId}`)
      .then(({ data }) => {
        if (!alive) return;
        setDetail(data);
        setNote(data.note || "");
        setPrLink(data.pr_link || "");
        setNextStatus(data.status === "new" ? "in_progress" : data.status);
      })
      .catch((loadError) => {
        if (alive) setError(loadError?.response?.data?.detail || "Could not open that request.");
      });
    return () => { alive = false; };
  }, [allowed, selectedId]);

  const copyBrief = async () => {
    if (!detail) return;
    const brief = buildAgentBrief(detail, { assetOrigin: process.env.REACT_APP_BACKEND_URL || window.location.origin });
    try {
      await navigator.clipboard.writeText(brief);
    } catch {
      const area = document.createElement("textarea");
      area.value = brief;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const saveStatus = async (event) => {
    event.preventDefault();
    if (!detail) return;
    setError("");
    try {
      const { data } = await api.patch(`/edit-requests/${detail.id}`, {
        status: nextStatus,
        note,
        pr_link: prLink,
      });
      setDetail(data);
      await load();
    } catch (saveError) {
      setError(saveError?.response?.data?.detail || "Could not update the request.");
    }
  };

  if (!allowed) {
    return (
      <section className="er-page" data-edit-name="Edit requests">
        <h1>Edit requests</h1>
        <p>Edit requests are available to directors and admins.</p>
      </section>
    );
  }

  return (
    <section className="er-page" data-edit-name="Edit requests" data-testid="edit-requests-page">
      <header className="er-hero">
        <div>
          <small>DIRECTOR / ADMIN</small>
          <h1>Edit requests</h1>
          <p>Click-to-edit notes from the running app, with the page context and screenshots attached.</p>
        </div>
      </header>
      <div className="er-filters">
        <label>
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter by status">
            {STATUSES.map(([value, label]) => <option key={value || "all"} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          Product
          <select value={product} onChange={(event) => setProduct(event.target.value)} aria-label="Filter by product">
            {PRODUCTS.map(([value, label]) => <option key={value || "all-products"} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          Route
          <input value={route} onChange={(event) => setRoute(event.target.value)} placeholder="/linkforge" aria-label="Filter by route" />
        </label>
      </div>
      {error ? <p className="er-error">{String(error)}</p> : null}
      <div className="er-layout">
        <div className="er-list" role="list">
          {rows.length ? rows.map((row) => (
            <button
              key={row.id}
              type="button"
              role="listitem"
              className={row.id === selectedId ? "is-on" : ""}
              onClick={() => setSelectedId(row.id)}
            >
              <Shot path={row.crop_path || row.screenshot_path} alt="" />
              <span>
                <b>{row.page_title || row.route || "Untitled page"}</b>
                <small>{PRODUCT_LABELS[row.product] || row.product || "App"} · {row.route}</small>
                <em>{row.request_text}</em>
              </span>
              <strong className={`is-${row.status}`}>{row.status?.replace("_", " ")}</strong>
            </button>
          )) : <p className="er-empty">No edit requests yet. Turn on Edit mode and send one from any page.</p>}
        </div>
        {detail ? (
          <article ref={detailRef} className="er-detail" data-edit-name="Edit request detail">
            <header className="er-detail__header">
              <div>
                <small>{PRODUCT_LABELS[detail.product] || detail.product || "App"} · {detail.route}</small>
                <h2>{detail.page_title || "Untitled page"}</h2>
              </div>
              <button type="button" onClick={copyBrief}>{copied ? "Copied" : "Copy for agent"}</button>
            </header>
            <div className="er-detail__body">
              <p className="er-request">{detail.request_text}</p>
              <div className="er-shots" aria-label="Request screenshots">
                <Shot path={detail.screenshot_path} alt="Viewport screenshot with the selection outlined" expandable />
                {detail.crop_path ? <Shot path={detail.crop_path} alt="Close-up of the selection" expandable /> : null}
              </div>
              <details className="er-context">
                <summary>Page context and selections</summary>
                <dl>
                  <div><dt>Product</dt><dd>{PRODUCT_LABELS[detail.product] || detail.product || "unknown"}</dd></div>
                  <div><dt>URL</dt><dd>{detail.url}</dd></div>
                  <div><dt>Open target</dt><dd>{detail.open_target || "none"}</dd></div>
                  <div><dt>Selections</dt><dd>{(detail.selections || []).map((item) => item.label).filter(Boolean).join(", ") || "none"}</dd></div>
                </dl>
              </details>
            </div>
            <form className="er-detail__form" onSubmit={saveStatus}>
              <label>
                Status
                <select value={nextStatus} onChange={(event) => setNextStatus(event.target.value)} aria-label="Request status">
                  {STATUSES.filter(([value]) => value).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label>
                PR link
                <input value={prLink} onChange={(event) => setPrLink(event.target.value)} placeholder="https://github.com/…" />
              </label>
              <label className="er-detail__note">
                Note
                <textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
              </label>
              <button type="submit" className="er-save">Update status</button>
            </form>
          </article>
        ) : <article className="er-detail"><p className="er-empty">Select a request.</p></article>}
      </div>
    </section>
  );
}
